/**
 * @file EditorialCard.tsx
 * @description Карточка-контейнер по DS «Chaiberi Editorial» (phase08.5).
 *
 * Переписан с MUI на shadcn Card. API сохранён для обратной совместимости —
 * `EditorialCard.Header / Body / Footer`. Параметр `sx` (MUI prop) теперь
 * принимается, но игнорируется — для совместимости компиляции при поэтапной
 * миграции страниц; используй className для стилей.
 *
 * `floating` — мягкая ambient-тень `0 8px 24px var(--shadow-ambient)` (тон тени — токен темы).
 * `padded=false` — отключает внутренние отступы Body для full-bleed таблиц.
 */

import * as React from "react";

import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription, CardAction } from "./card";
import { cn } from "@/lib/utils";

interface EditorialCardHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  /**
   * Где живут действия на узком экране.
   *
   * `inline` (по умолчанию) — справа от заголовка: шапка остаётся сеткой
   * `1fr auto`, как во всех остальных карточках.
   *
   * `below-on-mobile` — на `<sm` заголовок занимает всю строку, блок действий
   * встаёт под ним и растягивается на всю ширину карточки. Нужно там, где
   * действий два и больше: колонка `auto` на 360px сжимается до ширины одной
   * кнопки, и группа переносит кнопки друг под друга, хотя суммарно они в
   * карточку помещаются (замечание QA 2026-09-21, карточка «Клиент» в заказе).
   */
  actionPlacement?: "inline" | "below-on-mobile";
}

function Header({
  title,
  subtitle,
  action,
  actionPlacement = "inline",
}: EditorialCardHeaderProps) {
  const stacked = actionPlacement === "below-on-mobile";
  return (
    // `!` обязателен: `grid-cols-[1fr_auto]` приходит из CardHeader с той же
    // специфичностью, и без важности победителя решал бы порядок вариантов.
    <CardHeader className={cn(stacked && "max-sm:grid-cols-1!")}>
      <CardTitle>{title}</CardTitle>
      {subtitle ? <CardDescription>{subtitle}</CardDescription> : null}
      {action ? (
        <CardAction
          className={cn(
            stacked &&
              "max-sm:col-start-1 max-sm:row-span-1 max-sm:row-start-auto max-sm:mt-2 max-sm:justify-self-stretch",
          )}
        >
          {action}
        </CardAction>
      ) : null}
    </CardHeader>
  );
}

interface BodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

function Body({ className, children, ...props }: BodyProps) {
  return (
    <CardContent className={cn(className)} {...props}>
      {children}
    </CardContent>
  );
}

function Footer({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <CardFooter className={cn("justify-start gap-2", className)} {...props}>
      {children}
    </CardFooter>
  );
}

interface EditorialCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  children: React.ReactNode;
  floating?: boolean;
  padded?: boolean;
  /** Принимается для backward compat (MUI sx); не используется. Используй className. */
  sx?: unknown;
}

function EditorialCardRoot({
  children,
  floating = false,
  padded = true,
  className,
  sx: _sx,
  ...props
}: EditorialCardProps) {
  return (
    <Card
      className={cn(
        floating && "shadow-[0_8px_24px_var(--shadow-ambient)]",
        !padded && "[&>[data-slot=card-content]]:p-0",
        className,
      )}
      {...props}
    >
      {children}
    </Card>
  );
}

export const EditorialCard = Object.assign(EditorialCardRoot, {
  Header,
  Body,
  Footer,
});
